import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormSection } from '../form/FormSection';

export type DeliveryPricing = 'WOLT' | 'OWN_COURIER';

/** Prog w formularzu - pola tekstowe, zeby puste pole nie zamienialo sie w 0. */
export interface OwnCourierRateRow {
  maxDistanceKm: string;
  fee: string;
}

export interface OwnCouriersValues {
  ownCouriersEnabled: boolean;
  ownCourierRates: OwnCourierRateRow[];
  deliveryPricing: DeliveryPricing;
}

export interface OwnCourierRate {
  maxDistanceKm: number;
  fee: number;
}

export interface OwnCouriersSettings {
  enabled: boolean;
  rates: OwnCourierRate[] | null;
  deliveryPricing: DeliveryPricing;
}

const EMPTY_ROW: OwnCourierRateRow = { maxDistanceKm: '', fee: '' };

// Zasieg dostawy z backendu (delivery.max-distance-km) - dalszy prog nigdy by sie nie przydal.
export const OWN_COURIER_MAX_DISTANCE_KM = 6;

export const OWN_COURIERS_INITIAL: OwnCouriersValues = {
  ownCouriersEnabled: false,
  ownCourierRates: [],
  deliveryPricing: 'WOLT',
};

export const toOwnCouriersValues = (settings?: OwnCouriersSettings | null): OwnCouriersValues =>
  settings
    ? {
        ownCouriersEnabled: settings.enabled,
        ownCourierRates: (settings.rates ?? []).map((rate) => ({
          maxDistanceKm: rate.maxDistanceKm.toString(),
          fee: rate.fee.toString(),
        })),
        deliveryPricing: settings.deliveryPricing ?? 'WOLT',
      }
    : OWN_COURIERS_INITIAL;

/**
 * Wylaczeni kurierzy: progi nie sa wysylane (null = backend ich nie rusza), zeby po ponownym
 * wlaczeniu wrocily. Wlaczeni: progi posortowane po odleglosci.
 */
export const toOwnCouriersPayload = (values: OwnCouriersValues): OwnCouriersSettings => ({
  enabled: values.ownCouriersEnabled,
  rates: values.ownCouriersEnabled
    ? values.ownCourierRates
        .map((row) => ({ maxDistanceKm: Number(row.maxDistanceKm), fee: Number(row.fee) }))
        .sort((a, b) => a.maxDistanceKm - b.maxDistanceKm)
    : null,
  deliveryPricing: values.ownCouriersEnabled ? values.deliveryPricing : 'WOLT',
});

// Backend przyjmuje kwoty i odleglosci z najwyzej 2 miejscami po przecinku (@Digits fraction = 2).
const MAX_DECIMALS = 2;

const hasAllowedDecimals = (raw: unknown) =>
  (
    String(raw ?? '')
      .trim()
      .split(/[.,]/)[1] ?? ''
  ).length <= MAX_DECIMALS;

/**
 * Te same zasady co w backendzie: co najmniej jeden prog, odleglosci bez powtorzen, do 6 km,
 * najwyzej 2 miejsca po przecinku.
 */
export const ownCouriersValidation = (
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const distanceInvalid = t('validation.ownCourierDistanceInvalid', {
    max: OWN_COURIER_MAX_DISTANCE_KM,
  });
  const tooManyDecimals = t('validation.ownCourierTooManyDecimals', { max: MAX_DECIMALS });

  return {
    ownCourierRates: Yup.array().when('ownCouriersEnabled', {
      is: true,
      then: () =>
        Yup.array()
          .of(
            Yup.object({
              maxDistanceKm: Yup.number()
                .typeError(distanceInvalid)
                .moreThan(0, distanceInvalid)
                .max(OWN_COURIER_MAX_DISTANCE_KM, distanceInvalid)
                .required(distanceInvalid)
                .test('decimals', tooManyDecimals, (_, ctx) =>
                  hasAllowedDecimals(ctx.originalValue),
                ),
              fee: Yup.number()
                .typeError(t('validation.ownCourierRateInvalid'))
                .min(0, t('validation.ownCourierRateInvalid'))
                .max(1000, t('validation.ownCourierRateInvalid'))
                .required(t('validation.ownCourierRateInvalid'))
                .test('decimals', tooManyDecimals, (_, ctx) =>
                  hasAllowedDecimals(ctx.originalValue),
                ),
            }),
          )
          .min(1, t('validation.ownCourierRatesRequired'))
          .test('unique-distance', t('validation.ownCourierRatesDuplicate'), (rows) => {
            const distances = (rows ?? []).map((row) => Number(row?.maxDistanceKm));
            return new Set(distances).size === distances.length;
          }),
      otherwise: (schema) => schema,
    }),
  };
};

interface Props {
  values: OwnCouriersValues;
  errors: any;
  touched: any;
  handleBlur: (e: React.FocusEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  /** Kod waluty lokalu (np. PLN) - pokazywany przy cenie progu. */
  currency?: string;
}

const sectionTitleSx = { fontWeight: 600, fontSize: 'var(--text-sm)' };
const hintSx = { fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' };
const columnLabelSx = { ...hintSx, flex: 1, maxWidth: 180 };

/** Wlasni kurierzy lokalu: cennik dostawy wg odleglosci i cena dostawy widoczna dla klienta. */
export const OwnCouriersFields: React.FC<Props> = ({
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  currency,
}) => {
  const { t } = useTranslation();
  const rows = values.ownCourierRates;
  const rowErrors = Array.isArray(errors.ownCourierRates) ? errors.ownCourierRates : [];
  const rowTouched = Array.isArray(touched.ownCourierRates) ? touched.ownCourierRates : [];
  const listError = typeof errors.ownCourierRates === 'string' ? errors.ownCourierRates : null;

  const setRows = (next: OwnCourierRateRow[]) => setFieldValue('ownCourierRates', next);

  const updateRow = (index: number, field: keyof OwnCourierRateRow, value: string) => {
    // Nie przyjmujemy wiecej miejsc po przecinku niz backend - dalsze cyfry po prostu sie nie wpisuja.
    if (!hasAllowedDecimals(value)) return;
    setRows(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const fieldError = (index: number, field: keyof OwnCourierRateRow): string | undefined =>
    rowTouched[index]?.[field] ? rowErrors[index]?.[field] : undefined;

  const pricingOptions: { value: DeliveryPricing; label: string; hint: string }[] = [
    { value: 'WOLT', label: t('ownCouriers.pricingWolt'), hint: t('ownCouriers.pricingWoltHint') },
    {
      value: 'OWN_COURIER',
      label: t('ownCouriers.pricingOwn'),
      hint: t('ownCouriers.pricingOwnHint'),
    },
  ];

  return (
    <FormSection
      title={t('ownCouriers.title')}
      description={t('ownCouriers.description')}
      action={
        <Switch
          name="ownCouriersEnabled"
          checked={values.ownCouriersEnabled}
          slotProps={{ input: { 'aria-label': t('ownCouriers.enabled') } }}
          onChange={(_, checked) => {
            setFieldValue('ownCouriersEnabled', checked);
            // Pierwsze wlaczenie - od razu pusty wiersz do wypelnienia.
            if (checked && rows.length === 0) setRows([EMPTY_ROW]);
          }}
        />
      }
    >
      {values.ownCouriersEnabled && (
        <Stack spacing="var(--space-4)">
          <Box>
            <Typography sx={sectionTitleSx}>{t('ownCouriers.ratesTitle')}</Typography>
            <Typography sx={{ ...hintSx, mb: 'var(--space-3)' }}>
              {t('ownCouriers.rateHint', { max: OWN_COURIER_MAX_DISTANCE_KM })}
            </Typography>

            <Stack direction="row" sx={{ gap: 'var(--space-2)', mb: 'var(--space-1)' }}>
              <Typography sx={columnLabelSx}>{t('ownCouriers.upToKm')}</Typography>
              <Typography sx={columnLabelSx}>{t('ownCouriers.fee')}</Typography>
            </Stack>

            <Stack spacing="var(--space-1)">
              {rows.map((row, index) => (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="flex-start"
                  sx={{ gap: 'var(--space-2)' }}
                >
                  <TextField
                    name={`ownCourierRates.${index}.maxDistanceKm`}
                    type="number"
                    size="small"
                    placeholder="3"
                    value={row.maxDistanceKm}
                    onChange={(e) => updateRow(index, 'maxDistanceKm', e.target.value)}
                    onBlur={handleBlur}
                    error={Boolean(fieldError(index, 'maxDistanceKm'))}
                    helperText={fieldError(index, 'maxDistanceKm')}
                    sx={{ flex: 1, maxWidth: 180 }}
                    slotProps={{
                      // Bez min/max/step przegladarki - zakres sprawdza Yup, z komunikatem pod polem.
                      htmlInput: { step: 'any', 'aria-label': t('ownCouriers.upToKm') },
                      input: { endAdornment: <InputAdornment position="end">km</InputAdornment> },
                    }}
                  />
                  <TextField
                    name={`ownCourierRates.${index}.fee`}
                    type="number"
                    size="small"
                    placeholder="0"
                    value={row.fee}
                    onChange={(e) => updateRow(index, 'fee', e.target.value)}
                    onBlur={handleBlur}
                    error={Boolean(fieldError(index, 'fee'))}
                    helperText={fieldError(index, 'fee')}
                    sx={{ flex: 1, maxWidth: 180 }}
                    slotProps={{
                      htmlInput: { step: 'any', 'aria-label': t('ownCouriers.fee') },
                      input: currency
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">{currency}</InputAdornment>
                            ),
                          }
                        : undefined,
                    }}
                  />
                  <IconButton
                    aria-label={t('ownCouriers.removeRate')}
                    onClick={() => setRows(rows.filter((_, i) => i !== index))}
                    size="small"
                    sx={{ mt: '4px', color: 'var(--color-text-muted)' }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            {listError && <FormHelperText error>{listError}</FormHelperText>}

            <Button
              size="small"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setRows([...rows, EMPTY_ROW])}
              disabled={rows.length >= 20}
              sx={{ mt: 'var(--space-2)', textTransform: 'none' }}
            >
              {t('ownCouriers.addRate')}
            </Button>
          </Box>

          <FormControl>
            <Typography sx={{ ...sectionTitleSx, mb: 'var(--space-2)' }}>
              {t('ownCouriers.customerPrice')}
            </Typography>
            <RadioGroup
              name="deliveryPricing"
              value={values.deliveryPricing}
              onChange={(_, value) => setFieldValue('deliveryPricing', value)}
              sx={{ gap: 'var(--space-2)' }}
            >
              {pricingOptions.map((option) => {
                const selected = values.deliveryPricing === option.value;
                return (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" sx={{ mt: '-2px' }} />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                        <Typography sx={hintSx}>{option.hint}</Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      alignItems: 'flex-start',
                      p: 'var(--space-2) var(--space-3) var(--space-2) var(--space-1)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--color-primary)' : 'var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      bgcolor: selected ? 'var(--color-primary-soft)' : 'transparent',
                      transition: 'border-color 120ms, background-color 120ms',
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </Stack>
      )}
    </FormSection>
  );
};
